import { Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import './AdminLayout.scss';
import Header from 'components/UI/Header/Header';
import Sidebar from 'components/UI/Sidebar/Sidebar';
import Overlay from 'components/UI/Overlay/Overlay';
import { ADMINISTRATION, AUTHOR, SUPER_ADMIN } from 'constants/roles'; //REVIEWER
import classnames from 'classnames';
import { selectUser } from 'store/slices/auth';

/**
 * AdminLayout component
 *
 * @description Provides the layout structure for admin pages including header, sidebar (for authorized roles),
 * and main content area. The sidebar is only displayed for users with specific roles (SUPER_ADMIN, ADMINISTRATION, AUTHOR).
 *
 * @returns {JSX.Element} The rendered AdminLayout component
 */
const AdminLayout = () => {
  // Get overlay state from Redux store
  const isOverlayOpen: boolean = useSelector(
    (state: any) => state.overlay.isOpen,
  );

  // Get current user from Redux store
  const user = useSelector(selectUser);

  /**
   * Determines if sidebar should be shown based on user roles
   * Only users with SUPER_ADMIN, ADMINISTRATION, or AUTHOR roles can see the sidebar
   */
  const showSidebar = [SUPER_ADMIN, ADMINISTRATION, AUTHOR].includes(
    // v8 ignore start -- WAIVER(MICRO): user.role is always present on an authenticated session; the '' fallback exists only to satisfy the optional user type
    user?.role || '',
    // v8 ignore stop -- WAIVER(MICRO)
  );

  /**
   * CSS class names for the main container
   * Adds 'no-sidebar' class when sidebar should not be displayed
   */
  const mainContainerClasses = classnames({
    'page-container': true,
    'no-sidebar': !showSidebar,
  });

  return (
    <>
      <Header showSidebar={showSidebar} />
      {showSidebar && <Sidebar />}
      <main className={mainContainerClasses}>
        {isOverlayOpen && <Overlay />}
        <Outlet />
      </main>
    </>
  );
};

export default AdminLayout;
